import type { FC } from "react";

import { cn } from "../../utils";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { Icon, type MaterialSymbol } from "../icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import type { PaginationProps } from "./pagination";
import { usePagination } from "./use-pagination";

export interface CondensedPaginationProps extends PaginationProps {
  loading?: boolean;
}

const formatCount = (value: number) => Intl.NumberFormat("en-us").format(value);

const PagerButton: FC<{
  disabled: boolean;
  icon: MaterialSymbol;
  label: string;
  onClick: () => void;
}> = ({ disabled, icon, label, onClick }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="xs" display="icon" aria-label={label} disabled={disabled} onClick={onClick}>
        <Icon icon={icon} />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export const CondensedPagination: FC<CondensedPaginationProps> = ({ className, label = "Rows", loading = false }) => {
  const { numPages, numRecords, page, rowsPerPage, rowsPerPageOptions, setPage, setRowsPerPage } = usePagination();

  // `PaginationProvider` pulls an out-of-range page back in from an effect; clamp so the render
  // before that lands does not read `2,451–137 of 137`.
  const currentPage = Math.min(Math.max(page, 1), numPages);
  const first = (currentPage - 1) * Number(rowsPerPage) + 1;
  const last = Math.min(currentPage * Number(rowsPerPage), numRecords);
  const rowsPerPageLabel = `${label} per page`;
  const rangeLabel = loading
    ? "Loading…"
    : numRecords === 0
      ? `No ${label.toLowerCase()}`
      : `${formatCount(first)}–${formatCount(last)} of ${formatCount(numRecords)}`;

  return (
    <nav aria-busy={loading} className={cn("flex items-center", className)}>
      <PagerButton
        icon="chevron_left"
        label="Previous page"
        disabled={loading || currentPage === 1}
        onClick={() => setPage((page) => (page > 1 ? page - 1 : 1))}
      />
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="xs" className="tabular-nums text-xs" disabled={loading}>
                {rangeLabel}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{rowsPerPageLabel}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent>
          <DropdownMenuLabel>{rowsPerPageLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={rowsPerPage}
            onValueChange={(value) => {
              void setRowsPerPage(value);
              void setPage(1);
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <DropdownMenuRadioItem key={option} value={option}>
                {option}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <PagerButton
        icon="chevron_right"
        label="Next page"
        disabled={loading || currentPage === numPages}
        onClick={() => setPage((page) => (page < numPages ? page + 1 : numPages))}
      />
    </nav>
  );
};
